import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { User, GraduationCap, Building2, Check, Loader2 } from "lucide-react";

const Onboarding = () => {
  const { t, user, authLoading, setRole } = useApp();
  const navigate = useNavigate();
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login");
  }, [user, authLoading, navigate]);

  const options = [
    { id: "player", icon: User, label: t.roles.player, desc: t.onboard.playerDesc, tag: t.onboard.free, color: "blue" },
    { id: "academy_agent", icon: GraduationCap, label: t.roles.academy_agent, desc: t.onboard.academyDesc, tag: t.onboard.trial, color: "emerald" },
    { id: "pro_team", icon: Building2, label: t.roles.pro_team, desc: t.onboard.proDesc, tag: t.onboard.trial, color: "orange" },
  ];

  const handleContinue = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await setRole(selected);
      if (selected === "player") navigate("/my-profile");
      else navigate("/pricing");
    } catch {
      setSaving(false);
    }
  };

  const ring = { blue: "border-blue-500 bg-blue-500/10", emerald: "border-emerald-500 bg-emerald-500/10", orange: "border-orange-500 bg-orange-500/10" };
  const iconC = { blue: "text-blue-400", emerald: "text-emerald-400", orange: "text-orange-400" };

  return (
    <div className="pt-16 min-h-screen relative overflow-hidden">
      <div className="absolute inset-0 hero-glow" />
      <div className="relative max-w-4xl mx-auto px-5 py-16">
        <div className="text-center mb-10">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-white">{t.onboard.title}</h1>
          <p className="text-gray-400 mt-3">{t.onboard.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {options.map((o) => {
            const active = selected === o.id;
            return (
              <button
                key={o.id}
                onClick={() => setSelected(o.id)}
                className={`text-left bg-[#0f1116] border rounded-2xl p-6 transition-all ${active ? ring[o.color] : "border-white/8 hover:border-white/20"}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <o.icon className={`w-6 h-6 ${iconC[o.color]}`} />
                  </div>
                  {active && <Check className={`w-5 h-5 ${iconC[o.color]}`} />}
                </div>
                <h3 className="font-semibold text-white">{o.label}</h3>
                <p className="text-sm text-gray-400 mt-2 leading-relaxed">{o.desc}</p>
                <span className={`inline-block mt-4 text-xs font-medium px-2.5 py-1 rounded-full bg-white/5 ${iconC[o.color]}`}>{o.tag}</span>
              </button>
            );
          })}
        </div>

        <div className="flex justify-center mt-10">
          <Button onClick={handleContinue} disabled={!selected || saving} className="bg-blue-600 hover:bg-blue-500 text-white h-12 px-10 rounded-xl font-medium">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : t.onboard.continue}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
